import { useState, useEffect } from "react";
import { useIdentity } from "@/auth/identity";
import { toast } from "@/hooks/use-toast";
import { Truck } from "lucide-react";
import { tripService } from "@/services/api";
import { TripData } from "@/types";
import { useShiftDisplay } from "@/hooks/useShiftDisplay";
import { useDriver } from "@/context/DriverContext";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import BottomNavigation from "@/components/BottomNavigation";

const TripsHistory = () => {
  const { user } = useIdentity();
  const { isApiClientReady } = useDriver();
  const shiftDisplay = useShiftDisplay();
  const [trips, setTrips] = useState<TripData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTrips = async () => {
      try {
        const response = await tripService.getTrips();

        // Mostrar todos los viajes
        const allTrips = response.data.data;

        setTrips(allTrips);
      } catch (error: any) {
        let errorMessage = "No se pudieron cargar los viajes";

        if (error.response?.status === 500) {
          errorMessage = "Error del servidor. Intente más tarde.";
        } else if (error.response?.status === 404) {
          errorMessage = "No se encontraron viajes para este conductor.";
        } else if (error.response?.status === 401) {
          errorMessage =
            "Sesión expirada. Por favor, inicie sesión nuevamente.";
        } else if (!navigator.onLine) {
          errorMessage = "Sin conexión a internet. Verifique su conexión.";
        }

        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    if (user?.sub && isApiClientReady) {
      fetchTrips();
    } else if (!user?.sub || !isApiClientReady) {
      setLoading(false);
    }
  }, [user?.sub, isApiClientReady]);

  // Formatear fecha para zona horaria de Argentina (UTC-3)
  const formatDate = (dateString: string) => {
    // Crear fecha y ajustar para zona horaria de Argentina
    const date = new Date(dateString);

    // Ajustar para UTC-3 (Argentina)
    const argentinaDate = new Date(date.getTime() + 3 * 60 * 60 * 1000);

    return argentinaDate.toLocaleDateString("es-AR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-gray-100 pb-16">
      {/* Header */}
      <header className="bg-primary text-white p-4">
        <div className="container mx-auto flex items-center">
          <Truck className="h-5 w-5 mr-2" />
          <h1 className="text-xl font-bold">Historial de Viajes</h1>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto p-4">
        {loading ? (
          <div className="text-center py-10">Cargando viajes...</div>
        ) : trips.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-gray-500">No hay viajes</p>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow overflow-hidden mb-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Presente</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {trips.map((trip) => (
                    <TableRow key={trip.id}>
                      <TableCell className="font-medium">
                        <div>{formatDate(trip.date || "")}</div>
                        {/* Un chofer puede tener varias rutas el mismo día: zona y turno las distinguen */}
                        {(trip.svc?.name || trip.svc?.code || trip.shift) && (
                          <div className="text-xs font-normal text-gray-500" translate="no">
                            {[
                              trip.svc?.name || trip.svc?.code,
                              trip.shift ? shiftDisplay.forSvc(trip.shift, trip.svc?.id) : null,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {trip.attended ? (
                          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                            ✓ Presente
                          </Badge>
                        ) : (
                          <Badge
                            variant="secondary"
                            className="bg-red-100 text-red-800 hover:bg-red-100"
                          >
                            ✗ Ausente
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {trip.status === "PENDIENTE" && (
                          <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
                            ⏳ Pendiente
                          </Badge>
                        )}
                        {trip.status === "EN_CURSO" && (
                          <Badge className="bg-primary-soft text-primary-soft-foreground hover:bg-primary-soft">
                            🚚 En Curso
                          </Badge>
                        )}
                        {trip.status === "COMPLETADO" && (
                          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                            ✅ Completado
                          </Badge>
                        )}
                        {trip.status === "CANCELADO" && (
                          <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
                            ❌ Cancelado
                          </Badge>
                        )}
                        {trip.status === "ASIGNADO" && (
                          <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">
                            📋 Asignado
                          </Badge>
                        )}
                        {trip.status === "CONFIRMADO" && (
                          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                            ✓ Confirmado
                          </Badge>
                        )}
                        {trip.status === "CHECKEADO" && (
                          <Badge className="bg-primary-soft text-primary-soft-foreground hover:bg-primary-soft">
                            ✓ Checkeado
                          </Badge>
                        )}
                        {trip.status === "PROBLEMAS" && (
                          <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">
                            ⚠️ Problemas
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </main>

      <BottomNavigation />
    </div>
  );
};

export default TripsHistory;
